import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
  Spin,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import api from "../services/api";

const { Option } = Select;

const Players = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);

  // Upload modal
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ---------------------------
  // Fetch All Players
  // ---------------------------
  const fetchPlayers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/players");

      const finalData = Array.isArray(res.data)
        ? res.data
        : res.data.data || [];

      setPlayers(finalData);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch players");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
  }, []);

  // ---------------------------
  // Add / Edit Player Submit
  // ---------------------------
  const handleSubmit = async (values) => {
    try {
      if (editingPlayer) {
        await api.put(`/players/${editingPlayer.player_id}`, values);
        message.success("Player updated successfully");
      } else {
        await api.post("/players", values);
        message.success("Player added successfully");
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingPlayer(null);
      fetchPlayers();
    } catch (err) {
      console.error(err);
      message.error("Operation failed");
    }
  };

  // ---------------------------
  // Delete Player
  // ---------------------------
  const handleDelete = async (id) => {
    try {
      await api.delete(`/players/${id}`);
      message.success("Player deleted successfully");
      fetchPlayers();
    } catch (err) {
      console.error(err);
      message.error("Failed to delete player");
    }
  };

  // ---------------------------
  // Open Upload Modal
  // ---------------------------
  const handleOpenUploadModal = (player) => {
    setEditingPlayer(player);
    setIsUploadModalVisible(true);
  };

  // ---------------------------
  // Upload Handler
  // ---------------------------
  const handleUpload = async ({ file, onSuccess, onError }) => {
    if (!editingPlayer) return;

    const formData = new FormData();
    formData.append("profile_pic", file);

    try {
      setUploading(true);

      const res = await api.post(
        `/players/${editingPlayer.player_id}/upload-profile`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (res.data.success) {
        message.success("Profile picture updated!");
        fetchPlayers();
        onSuccess?.(res);
        setIsUploadModalVisible(false);
      } else {
        message.error("Upload failed");
        onError?.(new Error("Upload failed"));
      }
    } catch (error) {
      console.error(error);
      message.error("Upload failed");
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  // ---------------------------
  // Table Columns
  // ---------------------------
  const columns = [
    {
      title: "Profile",
      dataIndex: "profile_pic",
      key: "profile_pic",
      render: (url, record) =>
        url ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img
              src={url}
              alt="Profile"
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
            <Button size="small" onClick={() => handleOpenUploadModal(record)}>
              Change
            </Button>
          </div>
        ) : (
          <Button size="small" onClick={() => handleOpenUploadModal(record)}>
            Upload
          </Button>
        ),
    },
    { title: "ID", dataIndex: "player_id", key: "player_id" },
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (text) => text.charAt(0).toUpperCase() + text.slice(1),
    },
    { title: "Country", dataIndex: "country", key: "country" },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Button
            type="link"
            onClick={() => {
              setEditingPlayer(record);
              form.setFieldsValue(record);
              setIsModalVisible(true);
            }}
          >
            Edit
          </Button>

          <Popconfirm
            title="Are you sure to delete this player?"
            onConfirm={() => handleDelete(record.player_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <h2>Players Management</h2>

        <Button
          type="primary"
          onClick={() => {
            setEditingPlayer(null);
            form.resetFields();
            setIsModalVisible(true);
          }}
        >
          + Add Player
        </Button>
      </div>

      {loading ? (
        <Spin tip="Loading players..." />
      ) : (
        <Table columns={columns} dataSource={players} rowKey="player_id" bordered />
      )}

      {/* Add / Edit Modal */}
      <Modal
        title={editingPlayer ? "Edit Player" : "Add Player"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        okText={editingPlayer ? "Update" : "Add"}
        onOk={() => form.submit()}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ role: "batsman" }}
        >
          <Form.Item
            label="Player Name"
            name="name"
            rules={[{ required: true, message: "Please enter player name" }]}
          >
            <Input placeholder="Enter player name" />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: "Please select role" }]}
          >
            <Select>
              <Option value="batsman">Batsman</Option>
              <Option value="bowler">Bowler</Option>
              <Option value="allrounder">Allrounder</Option>
              <Option value="wicketkeeper">Wicketkeeper</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Country" name="country">
            <Input placeholder="Enter country name" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Profile Modal */}
      <Modal
        title="Upload Profile Picture"
        open={isUploadModalVisible}
        onCancel={() => setIsUploadModalVisible(false)}
        footer={null}
      >
        {editingPlayer && (
          <div style={{ textAlign: "center" }}>
            {editingPlayer.profile_pic && (
              <img
                src={editingPlayer.profile_pic}
                alt="Current"
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  objectFit: "cover",
                  marginBottom: 16,
                }}
              />
            )}

            <Upload
              customRequest={handleUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />} loading={uploading}>
                {uploading ? "Uploading..." : "Upload New Picture"}
              </Button>
            </Upload>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Players;
